import { graphic as ecGraphic, format as ecFormat } from "echarts";

export const option = {
    legend: {
        top: 10,
        data: ["Pan height", "Pan height (smoothed)", "Contact wire", "Catenary wire", "Unclassified wire", "Vertical acceleration"],
    },
    tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        // formatter: tooltipFormatter, // dynamic
    },
    axisPointer: [
        {
            link: [
                {
                    xAxisId: ["overview_x", "position_x"],
                },
            ],
        },
    ],
    grid: [
        { id: "height_grid", top: "5%", left: 80, right: 20, height: "27.5%" },
        { id: "stagger_grid", top: "35%", left: 80, right: 20, height: "27.5%" },
        { id: "acceleration_grid", top: "65%", left: 80, right: 20, height: "27.5%" },
        { id: "position_grid", top: "4.5%", left: 80, right: 20, bottom: "7%" },
        { id: "overview_grid", top: "93.5%", left: 80, right: 20, height: "5.5%" },
    ],
    xAxis: [
        {
            id: "height_x",
            gridId: "height_grid",
            type: "time",
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: { show: false },
            axisPointer: { label: { show: false }, lineStyle: { opacity: 0 } },
        },
        {
            id: "stagger_x",
            gridId: "stagger_grid",
            type: "time",
            axisLine: { onZero: false, offset: { y: -550 }, lineStyle: { opacity: 0 } },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: { show: false },
            axisPointer: { label: { show: false }, lineStyle: { opacity: 0 } },
        },
        {
            id: "acceleration_x",
            gridId: "acceleration_grid",
            type: "time",
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: { show: false },
            axisPointer: { label: { show: false }, lineStyle: { opacity: 0 } },
        },
        {
            id: "position_x",
            gridId: "position_grid",
            type: "time",
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: { show: false },
            axisPointer: { label: { show: false } },
        },
        {
            id: "overview_x",
            type: "time",
            min: "dataMin",
            max: "dataMax",
            gridId: "overview_grid",
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: { show: false },
            axisPointer: {
                show: true,
                snap: false,
                label: { show: false },
            },
            tooltip: { show: false },
        },
    ],
    yAxis: [
        {
            id: "height_y",
            gridId: "height_grid",
            type: "value",
            name: "Pan Height (mm)",
            nameLocation: "center",
            nameTextStyle: { fontSize: 14, fontWeight: "bold" },
            nameGap: 50,
            nameRotate: 90,
            min: 0, // dynamic
            max: 6500,
            axisPointer: { show: false },
        },
        {
            id: "stagger_y",
            gridId: "stagger_grid",
            type: "value",
            name: "Pan Stagger (mm)",
            inverse: true,
            nameLocation: "center",
            nameTextStyle: { fontSize: 14, fontWeight: "bold" },
            nameGap: 50,
            nameRotate: 90,
            min: -550,
            max: 550,
            axisPointer: { show: false },
        },
        {
            id: "acceleration_y",
            gridId: "acceleration_grid",
            type: "value",
            name: "Acceleration (mm/s²)",
            nameLocation: "center",
            nameTextStyle: { fontSize: 14, fontWeight: "bold" },
            nameGap: 50,
            nameRotate: 90,
            min: -12000,
            max: 12000,
            axisPointer: { show: false },
        },
        {
            id: "position_y",
            show: false,
            gridId: "position_grid",
            type: "value",
            name: "Current Position",
            min: 0,
            max: 1,
            axisPointer: { show: false },
        },
        {
            id: "overview_y",
            min: 0,
            max: 1,
            gridId: "overview_grid",
            axisLine: { show: false, onZero: false },
            axisTick: { show: false },
            axisLabel: { show: false },
            splitLine: { show: false },
            axisPointer: { show: false },
        },
    ],
    dataset: [
        {
            id: "overview_dataset",
            source: [],
            dimensions: [
                { name: "name", type: "ordinal" },
                { name: "startTimestamp", type: "time" },
                { name: "endTimestamp", type: "time" },
                { name: "startPosition", type: "number" },
                { name: "endPosition", type: "number" },
            ],
        },
    ],
    series: [
        {
            type: "custom",
            id: "selected_timestamp_position_series",
            xAxisId: "position_x",
            yAxisId: "position_y",
            data: [],
            renderItem: function (params, api) {
                const index = api.value(0);
                const startPoint = api.coord([index, 1]);
                const height = api.size([0, 1])[1];

                const bounds = ecGraphic.clipRectByRect(
                    {
                        x: startPoint[0],
                        y: startPoint[1],
                        width: 0,
                        height: height,
                    },
                    {
                        x: params.coordSys.x,
                        y: params.coordSys.y,
                        width: params.coordSys.width,
                        height: params.coordSys.height,
                    },
                );

                return (
                    bounds && {
                        type: "line",
                        transition: ["shape"],
                        emphasisDisabled: true,
                        shape: {
                            x1: bounds.x,
                            x2: bounds.x,
                            y1: bounds.y,
                            y2: bounds.y + bounds.height,
                        },
                        style: {
                            stroke: "red",
                            lineWidth: 1,
                        },
                    }
                );
            },
            emphasis: {
                disabled: true,
            },
            animation: false,
            tooltip: { show: false },
        },
        {
            type: "line",
            id: "height_mark_area_series",
            xAxisId: "height_x",
            yAxisId: "height_y",
            data: [],
            markArea: {
                silent: true,
                itemStyle: { opacity: 0.3 },
                data: [], // dynamic
            },
            showSymbol: false,
            lineStyle: { opacity: 0 },
            tooltip: { show: false },
            silent: true,
            animation: false,
        },
        {
            type: "bar",
            id: "height_pole_event_series",
            xAxisId: "height_x",
            yAxisId: "height_y",
            barWidth: 1,
            data: [],
            showSymbol: false,
            itemStyle: {
                color: "mediumorchid",
            },
            tooltip: { show: false },
            silent: true,
            animation: false,
        },
        {
            type: "scatter",
            id: "height_series",
            name: "Pan height",
            xAxisId: "height_x",
            yAxisId: "height_y",
            symbolSize: 6,
            data: [],
            itemStyle: {
                color: "#ff7f0e", // Orange
            },
            large: true,
            silent: true,
            animation: false,
            emphasis: { disabled: true },
        },
        {
            type: "line",
            id: "height_smooth_series",
            name: "Pan height (smoothed)",
            xAxisId: "height_x",
            yAxisId: "height_y",
            symbol: "none",
            data: [],
            itemStyle: {
                width: 2,
                color: "brown",
            },
            silent: true,
            animation: false,
        },
        {
            type: "line",
            id: "stagger_mark_area_series",
            xAxisId: "stagger_x",
            yAxisId: "stagger_y",
            data: [],
            markArea: {
                silent: true,
                itemStyle: { opacity: 0.15 },
                data: [], // dynamic
            },
            showSymbol: false,
            lineStyle: { opacity: 0 },
            tooltip: { show: false },
            silent: true,
            animation: false,
        },
        {
            type: "bar",
            id: "stagger_pole_event_series",
            xAxisId: "stagger_x",
            yAxisId: "stagger_y",
            barWidth: 1,
            data: [],
            itemStyle: {
                color: "mediumorchid",
            },
            silent: true,
            animation: false,
            tooltip: { show: false },
        },
        {
            type: "scatter",
            id: "stagger_contact_wire_series",
            name: "Contact wire",
            xAxisId: "stagger_x",
            yAxisId: "stagger_y",
            symbolSize: 3,
            data: [],
            itemStyle: {
                color: "#2ca02c", // Green
            },
            large: true,
            silent: true,
            animation: false,
            emphasis: { disabled: true },
        },
        {
            type: "scatter",
            id: "stagger_unclassified_wire_series",
            name: "Unclassified wire",
            xAxisId: "stagger_x",
            yAxisId: "stagger_y",
            symbolSize: 3,
            data: [],
            itemStyle: {
                color: "#1f77b4", // Blue
            },
            large: true,
            silent: true,
            animation: false,
            emphasis: { disabled: true },
        },
        {
            type: "scatter",
            id: "stagger_catenary_wire_series",
            name: "Catenary wire",
            xAxisId: "stagger_x",
            yAxisId: "stagger_y",
            symbolSize: 3,
            data: [],
            itemStyle: {
                color: "#d62728", // Red
            },
            large: true,
            silent: true,
            animation: false,
            emphasis: { disabled: true },
        },
        {
            type: "line",
            id: "acceleration_mark_area_series",
            xAxisId: "acceleration_x",
            yAxisId: "acceleration_y",
            data: [],
            markArea: {
                silent: true,
                itemStyle: { opacity: 0.3 },
                data: [
                    [
                        { yAxis: 12000, itemStyle: { color: "red" } },
                        { yAxis: 10000, itemStyle: { color: "red" } },
                    ],
                    [
                        { yAxis: 10000, itemStyle: { color: "green", opacity: 0.15 } },
                        { yAxis: -10000, itemStyle: { color: "green", opacity: 0.15 } },
                    ],
                    [
                        { yAxis: -10000, itemStyle: { color: "red" } },
                        { yAxis: -12000, itemStyle: { color: "red" } },
                    ],
                ],
            },
            showSymbol: false,
            lineStyle: { opacity: 0 },
            tooltip: { show: false },
            silent: true,
            animation: false,
        },
        {
            type: "bar",
            id: "acceleration_pole_event_series",
            xAxisId: "acceleration_x",
            yAxisId: "acceleration_y",
            barWidth: 1,
            data: [],
            showSymbol: false,
            itemStyle: {
                color: "mediumorchid",
            },
            tooltip: { show: false },
            silent: true,
            animation: false,
        },
        {
            type: "line",
            id: "acceleration_series",
            name: "Vertical acceleration",
            xAxisId: "acceleration_x",
            yAxisId: "acceleration_y",
            symbol: "none",
            data: [],
            itemStyle: {
                color: "#ff7f0e", // Orange
            },
            silent: true,
            animation: false,
        },
        {
            type: "custom",
            id: "overview_series",
            datasetId: "overview_dataset",
            xAxisId: "overview_x",
            yAxisId: "overview_y",
            encode: {
                x: ["startTimestamp", "endTimestamp"],
                value: ["name", "startTimestamp", "endTimestamp", "startPosition", "endPosition"],
            },
            renderItem: (params, api) => {
                const routeName = api.value(0);
                const startTimestamp = api.value(1);
                const endTimestamp = api.value(2);
                const startPosition = api.value(3);
                const endPosition = api.value(4);

                const startMile = Math.floor(startPosition / 80);
                const endMile = Math.floor(endPosition / 80);

                const startChain = Math.floor((startPosition - startMile * 80) / 10);
                const endChain = Math.floor((endPosition - endMile * 80) / 10);

                const startPoint = api.coord([startTimestamp, 0.75]);
                const endPoint = api.coord([endTimestamp, 0.25]);

                const bounds = ecGraphic.clipRectByRect(
                    {
                        x: startPoint[0],
                        y: startPoint[1],
                        width: endPoint[0] - startPoint[0],
                        height: endPoint[1] - startPoint[1],
                    },
                    {
                        x: params.coordSys.x,
                        y: params.coordSys.y,
                        width: params.coordSys.width,
                        height: params.coordSys.height,
                    },
                );

                if (!bounds) {
                    return null;
                }

                const fullElrText = `${routeName} ${startMile}.${startChain} - ${endMile}.${endChain}`;
                const elrText = `${routeName}`;
                const fullTextWidth = ecFormat.getTextRect(fullElrText).width;
                const smallTextWidth = ecFormat.getTextRect(elrText).width;

                let text = "";
                if (bounds.width > fullTextWidth + 4) {
                    text = fullElrText;
                } else if (bounds.width > smallTextWidth + 4) {
                    text = elrText;
                }

                return {
                    type: "group",
                    transition: ["shape"],
                    children: [
                        {
                            type: "line",
                            transition: ["shape"],
                            ignore: !bounds,
                            shape: {
                                x1: bounds.x,
                                y1: bounds.y,
                                x2: bounds.x,
                                y2: bounds.y + bounds.height,
                            },
                            emphasis: { disabled: true },
                            style: {
                                stroke: "#000",
                                lineWidth: 1,
                            },
                        },
                        {
                            type: "rect",
                            transition: ["shape"],
                            ignore: !bounds,
                            shape: bounds,
                            emphasis: { disabled: true },
                            style: {
                                text,
                                fill: "rgba(255, 255, 255, 0.5)",
                                textFill: "#000",
                            },
                        },
                    ],
                };
            },
            emphasis: { disabled: true },
            animation: false,
            tooltip: { show: false },
        },
        {
            type: "custom",
            id: `current_view_series`,
            xAxisId: "overview_x",
            yAxisId: "overview_y",
            data: [],
            renderItem: function (params, api) {
                const startIndex = api.value(0);
                const endIndex = api.value(1);

                const startPoint = api.coord([startIndex, 0.85]);
                const endPoint = api.coord([endIndex, 0.15]);

                const bounds = ecGraphic.clipRectByRect(
                    {
                        x: startPoint[0],
                        y: startPoint[1],
                        width: endPoint[0] - startPoint[0],
                        height: endPoint[1] - startPoint[1],
                    },
                    {
                        x: params.coordSys.x,
                        y: params.coordSys.y,
                        width: params.coordSys.width,
                        height: params.coordSys.height,
                    },
                );

                return (
                    bounds && {
                        type: "rect",
                        ignore: !bounds,
                        transition: ["shape"],
                        shape: bounds,
                        emphasis: { disabled: true },
                        style: {
                            fill: "rgba(220,192,255,0.5)",
                            stroke: "rgba(220,192,255,0.9)",
                        },
                    }
                );
            },
            emphasis: { disabled: true },
            animation: false,
            tooltip: { show: false },
        },
        {
            type: "custom",
            id: "selected_timestamp_overview_series",
            xAxisId: "overview_x",
            yAxisId: "overview_y",
            data: [],
            renderItem: function (params, api) {
                const index = api.value(0);

                const startPoint = api.coord([index, 0.9]);
                const endPoint = api.coord([index, 0.1]);

                const bounds = ecGraphic.clipRectByRect(
                    {
                        x: startPoint[0],
                        y: startPoint[1],
                        width: 0,
                        height: endPoint[1] - startPoint[1],
                    },
                    {
                        x: params.coordSys.x,
                        y: params.coordSys.y,
                        width: params.coordSys.width,
                        height: params.coordSys.height,
                    },
                );

                return (
                    bounds && {
                        type: "line",
                        transition: ["shape"],
                        emphasisDisabled: true,
                        shape: {
                            x1: bounds.x,
                            x2: bounds.x,
                            y1: bounds.y,
                            y2: bounds.y + bounds.height,
                        },
                        style: {
                            stroke: "red",
                            lineWidth: 1,
                        },
                    }
                );
            },
            emphasis: {
                disabled: true,
            },
            animation: false,
            tooltip: { show: false },
        },
    ],
};
